import Title_ru_RU                from "@/core/misc/decorators/Title_ru_RU";
import AdditionalFields, {field}  from "@/core/misc/decorators/AdditionalFields";
import Title_ru_RU_Class          from "@/core/misc/decorators/Title_ru_RU_Class";

import TYPES                      from "@/core/services/Changelogs/types";

import Core                       from "@/core/Core";
import BaseEntity                 from "@/core/misc/BaseEntity";
import Country                    from "@/domains/Countries/Country";
import Partner                    from "@/domains/Partners/Partner";
import CompanyTaxSystem           from "@/domains/CompanyTaxSystems/CompanyTaxSystem";
import CompanyAdditionalAttribute from "@/domains/Companies/CompanyAdditionalAttribute";
import BankAccount                from "@/domains/BankAccounts/BankAccount";
import CompaniesService           from "@/domains/Companies/CompaniesService";
import ProductionSite             from "@/domains/ProductionSites/ProductionSite";
import ProductionSitesService     from "@/domains/ProductionSites/ProductionSitesService";
import EdiOperatorConnection      from "@/domains/EdiOperators/EdiOperatorConnection";
import Store                      from "@/domains/Stores/Store";
import VatRate                    from "@/core/types/VatRate";

@AdditionalFields({
  inn: field("ИНН", TYPES.STRING),
  ogrn: field("ОГРН", TYPES.STRING),
  kpp: field("КПП", TYPES.STRING),
  nameShort: field("Краткое наименование", TYPES.STRING),
  nameFull: field("Полное наименование", TYPES.STRING),
  addressPostal: field("Почтовый адрес", TYPES.STRING),
  addressDeFacto: field("Фактический адрес", TYPES.STRING),
  addressDeJure: field("Юридический адрес", TYPES.STRING),

})
@Title_ru_RU_Class('Компания')
class Company extends BaseEntity {
  static Events = {
    Created: 'company:created',
    Updated: 'company:updated',
    Deleted: 'company:deleted'
  } as const;

  /** наименование */
  @Title_ru_RU('Название', TYPES.STRING)
  public name: string;

  /** страна */
  @Title_ru_RU('Страна', TYPES.OBJECT)
  public country: Country | null;

  /** партнер */
  @Title_ru_RU('Партнер', TYPES.OBJECT)
  public partner: Partner | null;

  /** использует EDI */
  @Title_ru_RU('Использует EDI', TYPES.BOOLEAN)
  public _isUsingEdi: boolean;
  /** EDI оператор */
  @Title_ru_RU('Связанные EDI операторы + внешний айди юрлица', TYPES.OBJECT)
  public ediConnections: EdiOperatorConnection[];

  /** использует документ заказа */
  @Title_ru_RU('Использует документ заказа', TYPES.BOOLEAN)
  public isUsingOrderDocument: boolean;

  /** система налогообложения */
  @Title_ru_RU('Система налогообложения', TYPES.OBJECT)
  public taxSystem: CompanyTaxSystem | null;
  /** является плательщиком НДС */
  @Title_ru_RU('Плательщик НДС', TYPES.BOOLEAN)
  public _isVatPayer: boolean;
  /** ставка НДС для компании */
  /** С 2025 года, УСН подразумевает какой-то процент, потому мы решили
   * сунуть это сюда */
  @Title_ru_RU('Ставка НДС', TYPES.NUMBER)
  public vatRate: VatRate | null;

  /** дополнительные атрибуты */
  @Title_ru_RU('Дополнительные атрибуты', TYPES.OBJECT)
  public additionalAttributes: Record<string, CompanyAdditionalAttribute> | null;

  /** банковские счета */
  @Title_ru_RU('Банковские счета', TYPES.OBJECT)
  public bankAccounts: BankAccount[];

  /** id компании в базе ЛСЗ (таблица tehno.finclient) */
  @Title_ru_RU('ID компании в базе ЛСЗ', TYPES.NUMBER)
  public swFinClientId: number;

  /** склад доставки по умолчанию: подставляется в заказ при выборе
   * этого юр. лица грузополучателем */
  @Title_ru_RU('Склад доставки по умолчанию', TYPES.OBJECT)
  public defaultStore: Store | null;

  /** За каждым юр лицом закреплён свой набор площадок */
  public productionSites: Array<ProductionSite>;

  /** чтобы установить страну и получить атрибуты нужны данные страны партнера */
  static fromServer(data: any, result = new Company()): Company {
    super.fromServer(data, result);

    result.country = Core.utils.hydrate(data.country, Country.fromServer);
    result.partner = Core.utils.hydrate(data.partner, Partner.fromServer);
    result.ediConnections = Core.utils.hydrateArray(data.ediConnections, EdiOperatorConnection.fromServer);
    result.taxSystem = Core.utils.hydrate(data.taxSystem, CompanyTaxSystem.fromServer);
    result.vatRate = Core.utils.hydrate(data.vatRate, VatRate.fromServer);

    result.additionalAttributes =
      Core.utils.isObject(data.additionalAttributes)
        ? Object.fromEntries(
          Object.entries(data.additionalAttributes).map(([key, val]) => [
            key,
            Core.utils.hydrate(val, CompanyAdditionalAttribute.fromServer)
          ])
        )
        : {};

    result.bankAccounts = Core.utils.hydrateArray(data.bankAccounts, BankAccount.fromServer);

    result.defaultStore = Core.utils.hydrate(data.defaultStore, Store.fromServer);

    return result;
  }

  /**
   * Новая компания партнёра.
   *
   * Страна передаётся вторым параметром, а не выставляется после создания:
   * реквизиты (additionalAttributes) зависят от страны и грузятся здесь же.
   * Если создать компанию со страной партнёра и потом сменить страну, уйдут
   * два запроса — сначала реквизиты страны партнёра (лишние), потом реквизиты
   * выбранной страны. Поэтому карточка при смене страны передаёт её сюда,
   * а без параметра берётся страна партнёра.
   */
  static async fromPartner(partner: Partner, country: Country = partner.country): Promise<Company> {
    const company = new this();

    company.partner = partner;
    company.country = country;

    // company.isVatPayer = true;

    /** Иностранцы для нас всегда не плательщики НДС. Пишем в поле напрямую:
     *  сеттер isVatPayer пропускает страны кроме RU и UZ. */
    if (Core.currentPartner.country.code !== country.code) {
      company._isVatPayer = false;
    }
    else {
      // своя страна — признак выставит галка по системе налогообложения
    }

    // Склада по умолчанию у новой компании нет — отсутствие явное.
    company.defaultStore = null;

    company.additionalAttributes = await CompaniesService.getAdditionalAttributes(country.code);

    return company;
  }

  /**
   * Ставки НДС, доступные компании.
   * Узбекистан — 12. Россия по системе налогообложения: ОСНО (id 1) — 22,
   * УСН (id 3) — 7, 5, 0 (с 2025 года у УСН свой набор ставок),
   * ЕСХН (id 4) — 22, 10, остальные — пусто.
   */
  get availableVatRates(): VatRate[] {
    if (this.country?.code === 'UZ') {
      return Core.dictionaries.vatRates.toArray().filter(rate => rate.id === 12);
    }
    // ОСНО
    else if (this.taxSystem?.id === 1) {
      return Core.dictionaries.vatRates.toArray().filter(rate => rate.id === 22);
    }
    // УСН
    else if (this.taxSystem?.id === 3) {
      return Core.dictionaries.vatRates.toArray().filter(rate => [7, 5, 0].includes(rate.id));
    }
    // ЕСХН
    else if (this.taxSystem?.id === 4) {
      return Core.dictionaries.vatRates.toArray().filter(rate => [22, 10].includes(rate.id));
    }
    else {
      return [];
    }
  }

  get isVatPayer() { return this._isVatPayer; }
  set isVatPayer(value) {
    // Только UZ и RU могут вводить НДС. Остальные пока не нужны
    if (this.country.code === 'UZ' || this.country.code === 'RU') {
      /** Ставку подставляет источник селекта в RU/UZAdditionalAttributes */
      this._isVatPayer = value;
    }
    else {
      // остальным странам признак не меняем
    }
  }


  get isUsingEdi() { return this._isUsingEdi }
  set isUsingEdi(value) {
    this._isUsingEdi = value;

    if (!this._isUsingEdi) {
      this.ediOperator = null;
    }
  }

  /**
   * Главный налоговый/регистрационный реквизит компании (ИНН, БИН, УНП, USCC, VAT и т.п.)
   * в виде строки "<название>: <значение>". Какой именно реквизит главный — определяет
   * флаг isTin на атрибуте (приходит с бэка из company_attribute_types.is_tin),
   * один на страну.
   *
   * Форматтер значения пока есть только для РФ (utils.regional.ru.formatINN);
   * для остальных стран значение выводится как есть.
   */
  getTin(): string {
    const attribute = Object.values(this.additionalAttributes ?? {})
      .find(attr => attr.isTin);

    if (Core.utils.isNullOrUndefined(attribute)) {
      return 'Не указан';
    }

    /** Реквизит у страны есть, но у компании не заполнен — иначе выводилось бы "УНП: null" */
    if (Core.utils.isNullOrUndefined(attribute.value) || attribute.value === '') {
      return `${attribute.name}: --`;
    }

    if (this.country?.code === 'RU') {
      return `${attribute.name}: ${Core.utils.regional.ru.formatINN(attribute.value)}`;
    } else {
      return `${attribute.name}: ${attribute.value}`;
    }
  }

  /**
   * Описание для отображения в селекторах (VSelect, option-description="description").
   * Показываем главный реквизит компании.
   */
  get description(): string {
    return this.getTin();
  }

  async loadProductionSites() {
    this.productionSites = await ProductionSitesService.getList(this.partner, this);
  }

  hasProductionSites() {
    return Core.utils.isNotEmptyArray(this.productionSites);
  }

  /**
   * Проверка перед сохранением. Возвращает список ошибок, пустой — ошибок нет.
   * Плательщику НДС ставка обязательна и должна быть из доступных ему.
   */
  validate(): string[] {
    const errors: string[] = [];

    if (this._isVatPayer === true) {
      if (Core.utils.isNullOrUndefined(this.vatRate) === true) {
        errors.push('Не выбрана ставка НДС');
      }
      else if (this.availableVatRates.some(rate => rate.id === this.vatRate!.id) === false) {
        errors.push(`Ставка НДС ${this.vatRate} недоступна для выбранной системы налогообложения`);
      }
    }

    return errors;
  }
}

export default Company;